import { Resources } from 'constants/resources';

import { useGatewaysCurrencyIds } from 'hooks/useGatewaysCurrencyIds';
import { useFormContext } from 'react-hook-form';
import { FilterFieldConfig } from 'shared/mui/Filter/config';
import {
  AutocompleteInput,
  AutocompleteMultiselectInput,
  EnumAutocompleteInput,
} from 'shared/react-admin/Inputs';
import { ReferenceArrayInput, ReferenceInput } from 'shared/react-admin/ReferenceInputs';
import { Currency } from 'types/embedded/currency';
import { cleanEmpty } from 'utils/cleanEmpty';

type PresetsListFilterFieldsParams = {
  resetDependentOnChange: boolean;
};

export const usePresetsListFilterFields = ({
  resetDependentOnChange,
}: PresetsListFilterFieldsParams): FilterFieldConfig[] => {
  const { watch, resetField } = useFormContext();
  const [merchantWatched, gatewayWatched] = watch(['merchant', 'gateway']);
  const currencyIds = useGatewaysCurrencyIds(gatewayWatched ?? []);

  return [
    {
      name: 'merchant',
      label: 'Мерчант',
      chipType: 'autocomplete',
      chipProps: {
        resource: Resources.Merchants,
      },
      input: (
        <ReferenceInput isFilter perPage={200} reference={Resources.Merchants} source="merchant">
          <AutocompleteInput
            filterToQuery={(searchText: string) => ({ name: searchText })}
            helperText={false}
            label="Мерчант"
            onChange={
              resetDependentOnChange
                ? () => {
                    resetField('gateway', { defaultValue: [] });
                    resetField('currency', { defaultValue: [] });
                  }
                : undefined
            }
            optionText="name"
            variant="outlined"
          />
        </ReferenceInput>
      ),
    },
    {
      name: 'gateway',
      label: 'Шлюзы',
      chipType: 'multiselect',
      chipProps: {
        resource: Resources.Gateways,
        limitTags: 1,
      },
      input: (
        <ReferenceArrayInput
          filter={cleanEmpty({ 'merchantAccounts.merchants': merchantWatched || undefined })}
          perPage={200}
          reference={Resources.Gateways}
          source="gateway"
        >
          <AutocompleteMultiselectInput
            filterToQuery={(searchText: string) => ({ name: searchText })}
            helperText={false}
            label="Шлюзы"
            name="gateway"
            onChange={
              resetDependentOnChange
                ? () => {
                    resetField('currency', { defaultValue: [] });
                  }
                : undefined
            }
            optionText="name"
          />
        </ReferenceArrayInput>
      ),
    },
    {
      name: 'currency',
      label: 'Валюты',
      chipType: 'multiselect',
      chipProps: {
        resource: Resources.Currencies,
        limitTags: 1,
      },
      input: (
        <ReferenceArrayInput
          filter={cleanEmpty({
            id: currencyIds.length ? currencyIds : undefined,
            active: true,
          })}
          perPage={50}
          reference={Resources.Currencies}
          source="currency"
        >
          <AutocompleteMultiselectInput
            filterToQuery={(searchText: string) => ({ currency: searchText })}
            getOptionLabel={(record: Currency) => `${record?.alphaCode}, ${record?.name}`}
            helperText={false}
            label="Валюты"
            name="currency"
          />
        </ReferenceArrayInput>
      ),
    },
    {
      name: 'presetItems.direction',
      label: 'Направление',
      chipType: 'enumAutocomplete',
      chipProps: {
        resource: Resources.Directions,
      },
      input: (
        <EnumAutocompleteInput
          helperText={false}
          label="Направление пресета"
          optionText="description"
          optionValue="value"
          resource={Resources.Directions}
          source="presetItems.direction"
          variant="outlined"
        />
      ),
    },
  ];
};
